import { ErrorCode } from '@novachat/shared';
import { ApiError, authCalls } from './api.js';
/** Refresh this long before the access token expires. */
const REFRESH_MARGIN_MS = 60_000;
/** Restoring a session while offline: try again after 1 s, 2 s, 4 s… then every 15 s. */
const RESTORE_RETRY_MS = 1_000;
const RESTORE_RETRY_MAX_MS = 15_000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isRejected = (error) => error instanceof ApiError && (error.status === 401 || error.status === 403);
export function createSession({ http, platform, deviceName, refreshStore, locks, now = Date.now, wait = sleep }) {
    const calls = authCalls(http);
    let state = { status: 'loading' };
    let accessToken = null;
    let expiresAt = 0;
    let refreshing = null;
    const listeners = new Set();
    function setState(next) {
        state = next;
        for (const listener of listeners)
            listener(state);
    }
    async function storeTokens(tokens) {
        accessToken = tokens.accessToken;
        expiresAt = now() + tokens.accessTokenExpiresIn * 1000;
        if (tokens.refreshToken && refreshStore)
            await refreshStore.set(tokens.refreshToken);
    }
    async function clear(reason) {
        accessToken = null;
        expiresAt = 0;
        await refreshStore?.set(null);
        setState({ status: 'signed-out', reason });
    }
    async function finish(response) {
        if (response.status === 'two_factor')
            return response;
        await storeTokens(response.tokens);
        setState({ status: 'signed-in', user: response.user });
        return { status: 'ok', user: response.user };
    }
    async function doRefresh() {
        const stored = refreshStore ? await refreshStore.get() : undefined;
        if (refreshStore && !stored) {
            await clear('signed-out');
            return null;
        }
        try {
            await storeTokens(await calls.refresh(stored ?? undefined));
            return accessToken;
        }
        catch (error) {
            if (isRejected(error)) {
                // Nothing to restore on first open is not an expiry: only say "expired" mid-session.
                const reason = error.code === ErrorCode.Forbidden ? 'no-access' : state.status === 'signed-in' ? 'expired' : 'signed-out';
                await clear(reason);
                return null;
            }
            throw error; // offline or server trouble: keep the session, try again later
        }
    }
    /**
     * One refresh at a time in this tab, and across tabs when a lock manager is available. Once
     * signed out, never: every call that met a 401 asked again, and a signed-out web app sent dozens
     * of refreshes a minute until the server rate-limited it (2026-11-04). Opening the app
     * (`start`) or signing in begins anew.
     */
    function refresh() {
        if (state.status === 'signed-out')
            return Promise.resolve(null);
        refreshing ??= (locks ? locks.request('novachat-refresh', doRefresh) : doRefresh()).finally(() => {
            refreshing = null;
        });
        return refreshing;
    }
    return {
        get state() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        /**
         * Restores a previous session (cookie or stored token) when the app opens. Offline or with the
         * server down it keeps waiting ('loading') and tries again: signing out here would make desktop
         * and mobile throw away a refresh token that is still good. Only a rejected token signs out.
         */
        async start() {
            for (let attempt = 0;; attempt++) {
                try {
                    const token = await refresh();
                    if (!token) {
                        if (state.status === 'loading')
                            await clear('signed-out');
                        return;
                    }
                    setState({ status: 'signed-in', user: await calls.me(token) });
                    return;
                }
                catch (error) {
                    if (isRejected(error)) {
                        await clear(error.code === ErrorCode.Forbidden ? 'no-access' : 'expired');
                        return;
                    }
                    await wait(Math.min(RESTORE_RETRY_MAX_MS, RESTORE_RETRY_MS * 2 ** attempt));
                }
            }
        },
        async login(email, password) {
            return finish(await calls.login({ email, password, deviceName, platform }));
        },
        async verifyTwoFactor(challengeToken, code) {
            return finish(await calls.verifyTwoFactor({ challengeToken, code, deviceName, platform }));
        },
        resendTwoFactor: calls.resendTwoFactor,
        /** A valid access token, refreshed first when it is about to expire; null when signed out. */
        async accessToken() {
            if (accessToken && now() < expiresAt - REFRESH_MARGIN_MS)
                return accessToken;
            if (state.status === 'signed-out')
                return null;
            return refresh();
        },
        /** Forces a refresh, e.g. after the server rejected the current access token. */
        refresh,
        /** Called when the server ended the session (e.g. WebSocket close 4003). */
        async ended(reason = 'expired') {
            await clear(reason);
        },
        async logout() {
            const token = accessToken;
            await clear('signed-out');
            if (token)
                await calls.logout(token).catch(() => undefined);
        },
    };
}
//# sourceMappingURL=session.js.map